/**
 * PowerPoint action bar — the buttons under a general PowerPoint reply
 * (AiVA for PowerPoint spec §5, SC-02…SC-05, and ST-08 Copy).
 *
 * The bar is built by the add-in when the turn ends, not emitted by the model.
 * Spec §5 puts it under EVERY reply, and every PowerPoint skill writes its
 * replies slide-ready (the §4.1 response contract, appended for the host), so
 * there is nothing for the model to decide. Only the content skills
 * (`ppt_draft_content`, `ppt_summarise_deck`) grant the bar's tools; the chat
 * fallback `ppt_general` grants none, like word_general (AIVA-9186).
 * Word shows what the alternative costs: its pill tools are
 * marked "MANDATORY" in the prompts because the model kept forgetting to call
 * them, and a forgotten call means a reply with no way to place it.
 *
 * What the skill DECLARES is still the authority: a row is only offered when
 * the turn's `allowed_tools` grants its tool id, so which skills get which
 * buttons stays a seed-data decision, exactly as with WORD_PILL_SPECS.
 *
 * If the model does call a pill tool, that call renders nothing (AIVA-8972,
 * integrations/tools/powerpoint/handlers.ts), so this bar is the only one and
 * its checks below always apply.
 *
 * One table, one row per button.
 */

import {
  NO_SLIDE_SELECTED_ERROR,
  NOTHING_TO_INSERT_ERROR,
  POWERPOINT_UNAVAILABLE_ERROR,
  SLIDE_FULL_ERROR,
} from '../../integrations/powerpoint/services/insertIntoSlide';
import { NOTES_UNAVAILABLE_ERROR } from '../../integrations/powerpoint/services/addSpeakerNotes';
import { ADD_SLIDE_UNAVAILABLE_ERROR } from '../../integrations/powerpoint/services/addAsNewSlide';
import { COPY_FAILED_ERROR } from '../../integrations/powerpoint/services/copyResponse';
import { NO_TEXT_SHAPE_SELECTED_ERROR } from '../../integrations/powerpoint/services/replaceSelection';
import { isNonInsertableOrFindings, isQuestionOrDecline } from '../hooks/useChatHelpers';
import type { Pill } from '../types';

export interface PowerPointPillSpec {
  /** ToolRegistry id the skill must list in `allowedTools` for this button. */
  readonly toolId: string;
  /** Office.js handler the click runs (see integrations/tools/powerpoint). */
  readonly handler: string;
  /** Button caption. */
  readonly label: string;
}

/** SC-02 — Insert into slide. */
export const PPT_INSERT_INTO_SLIDE: PowerPointPillSpec = {
  toolId: 'pill_ppt_insert_into_slide',
  handler: 'powerpoint.insert_text',
  label: 'Insert into slide',
};

/** SC-03 — Replace selection: the selected shape's text becomes the reply. */
export const PPT_REPLACE_SELECTION: PowerPointPillSpec = {
  toolId: 'pill_ppt_replace_selection',
  handler: 'powerpoint.replace_selection',
  label: 'Replace selection',
};

/** SC-04 — Add as speaker notes: appended to the current slide's notes. */
export const PPT_ADD_SPEAKER_NOTES: PowerPointPillSpec = {
  toolId: 'pill_ppt_add_speaker_notes',
  handler: 'powerpoint.add_speaker_notes',
  label: 'Add as speaker notes',
};

/** SC-05 — Add as new slide: line 1 the title, the rest the body, after the selected slide. */
export const PPT_ADD_AS_NEW_SLIDE: PowerPointPillSpec = {
  toolId: 'pill_ppt_add_as_new_slide',
  handler: 'powerpoint.add_as_new_slide',
  label: 'Add as new slide',
};

/** ST-08 — Copy: the raw reply text to the clipboard. */
export const PPT_COPY: PowerPointPillSpec = {
  toolId: 'pill_ppt_copy_text',
  handler: 'powerpoint.copy_text',
  label: 'Copy',
};

/** Every PowerPoint action-bar button, in the order the bar shows them. */
export const PPT_PILL_SPECS: readonly PowerPointPillSpec[] = [
  PPT_INSERT_INTO_SLIDE,
  PPT_REPLACE_SELECTION,
  PPT_ADD_SPEAKER_NOTES,
  PPT_ADD_AS_NEW_SLIDE,
  PPT_COPY,
];

export interface BuildPowerPointPillsInput {
  /** The turn's `allowed_tools` from the `done` event; undefined when not sent. */
  readonly allowedTools: readonly string[] | undefined;
  /** The finished reply, as streamed. */
  readonly answerText: string;
  /** The backend's `committable` flag; only an explicit `false` suppresses the bar. */
  readonly committable?: boolean;
  /**
   * A shape with text was selected on the slide when the turn was sent. Without
   * one, Replace selection is shown greyed out rather than hidden (spec §5).
   */
  readonly textShapeSelected?: boolean;
}

/** Tooltip on a greyed-out Replace selection (spec SC-03, AC-1). */
export const REPLACE_SELECTION_DISABLED_REASON = 'Select a shape with text in PowerPoint first';

/**
 * The bar's buttons, in the spec's order (§5, Figure 5.1): Insert into slide,
 * Replace selection, then the later rows. Every granted button is shown — the
 * spec's AC-2 counts them — and Replace selection is greyed out, with a tooltip
 * saying what to do, when no shape with text was selected (SC-03 AC-1:
 * "never enabled-then-failing").
 */
function barPill(spec: PowerPointPillSpec, text: string, textShapeSelected: boolean): Pill {
  const pill: Pill = { type: 'office_action', label: spec.label, handler: spec.handler, args: { text } };
  if (spec === PPT_REPLACE_SELECTION && !textShapeSelected) {
    return { ...pill, disabled: true, disabledReason: REPLACE_SELECTION_DISABLED_REASON };
  }
  return pill;
}

/**
 * G-10 (AIVA-8913) — re-gate an already-built bar's Replace selection on the
 * live selection, so selecting a shape after the reply arrived enables it (and
 * deselecting greys it out again). Every other pill is returned untouched, and
 * a bar without Replace selection is returned as is.
 */
export function withLiveReplaceSelection(pills: readonly Pill[], textShapeSelected: boolean): Pill[] {
  return pills.map((pill) => {
    if (pill.type !== 'office_action' || pill.handler !== PPT_REPLACE_SELECTION.handler) return pill;
    if (!textShapeSelected) return { ...pill, disabled: true, disabledReason: REPLACE_SELECTION_DISABLED_REASON };
    // A copy without the two gating fields; every other field (args, once…) is kept.
    const enabled = { ...pill };
    delete enabled.disabled;
    delete enabled.disabledReason;
    return enabled;
  });
}

/**
 * A reply with nothing in it to place or copy: a question back to the user
 * ("What should the new slide cover?"), a decline ("That's outside what I do…")
 * or anything else addressed to the user. AIVA-9186: no pills at all, Copy
 * included — the same rule Word's bar applies.
 */
function hasNoContent(text: string): boolean {
  return isQuestionOrDecline(text) || isNotSlideContent(text);
}

/**
 * The action-bar pills for a finished general PowerPoint turn.
 *
 * Empty when there is nothing to offer: no text, an error/guidance line, or a
 * reply with no content ({@link hasNoContent}). When the backend marks the turn
 * not committable, only Copy is offered — copying writes nothing to the slide.
 * Undefined `allowedTools` grants nothing: unlike Word there is no older
 * PowerPoint stream to stay compatible with, so "unknown" is never read as
 * "allowed".
 */
export function buildPowerPointPills(input: BuildPowerPointPillsInput): Pill[] {
  const text = input.answerText.trim();
  if (!text || isNonInsertableOrFindings(text) || hasNoContent(text)) return [];
  const committable = input.committable !== false;
  const granted = new Set(input.allowedTools ?? []);
  return PPT_PILL_SPECS.filter((spec) => granted.has(spec.toolId) && (committable || spec === PPT_COPY)).map(
    (spec) => barPill(spec, text, input.textShapeSelected ?? false)
  );
}

/** Longest reply the patterns below are allowed to judge. Slide content that
 *  happens to open with one of these words is longer; a request or decline is
 *  one or two sentences. Same cap idea as the Word decline detectors. */
const NOT_CONTENT_MAX_LENGTH = 320;

/** Optional polite lead-in before the part that matters. */
const LEAD_IN = String.raw`^(?:(?:sure|of course|certainly|sorry|unfortunately)[,.!—–-]*\s+)?`;

/** What makes "paste / share / send …" a request to the user rather than slide
 *  copy: it says where to put it, or what AiVA will do once it has it. A slide
 *  line such as "Share results with the board" says neither. */
const ADDRESSED_TO_USER = String.raw`[\s\S]*\b(?:here|below|in(?:to)?\s+(?:the\s+)?chat|I'll|I’ll|I\s+will|I\s+can|so\s+(?:that\s+)?I|and\s+I)\b`;

const REQUEST_FOR_INPUT_PATTERNS: readonly RegExp[] = [
  // "Please paste the slide text here, and I'll draft…" — a request for input.
  new RegExp(
    LEAD_IN + String.raw`(?:please\s+)?(?:paste|share|provide|send|upload|select)\b` + ADDRESSED_TO_USER,
    'i'
  ),
  // "Could you share the bullets so I can…" (the "?" form is caught upstream).
  new RegExp(
    LEAD_IN +
      String.raw`(?:could|can|would)\s+you\s+(?:please\s+)?(?:paste|share|provide|send|upload|select)\b` +
      ADDRESSED_TO_USER,
    'i'
  ),
];

const NOT_SLIDE_CONTENT_PATTERNS: readonly RegExp[] = [
  ...REQUEST_FOR_INPUT_PATTERNS,
  // "I can't see the slide…", "I'm unable to insert…" — AiVA about itself.
  new RegExp(
    LEAD_IN +
      String.raw`I(?:\s+am|'m)?\s+(?:can't|can’t|cannot|can\s+not|unable\s+to)\s+(?:see|read|access|insert|edit|change|open|view)\b`,
    'i'
  ),
  // The off-topic decline ppt_general's prompt asks for: "That's outside what I
  // do. I can draft a title slide…" (reported for "can you write a poem?").
  // Needs AiVA's own scope after it — "what I do / can help with", "my scope" —
  // so slide copy such as "This is beyond what we forecast" keeps its buttons.
  new RegExp(
    LEAD_IN +
      String.raw`(?:that|this|it)(?:'s|’s|\s+is)\s+(?:outside|beyond|not\s+within|not\s+part\s+of)\s+(?:what\s+I\s+(?:do|can)\b|my\s+(?:scope|remit|role)\b|the\s+scope\s+of\s+(?:what\s+I|AiVA|this)\b)`,
    'i'
  ),
  // The design hand-off and the AIVA-8792 "Deckorate isn't enabled" reply. The
  // model words it freely — "done in" or "handled in", and a curly ’ as often
  // as a straight ' ("it isn’t enabled") — so match the meaning, not one phrasing.
  /\bDeckorate\b[^.]{0,80}\b(?:isn['’]?t|is\s+not|not)\s+enabled\b|\bslide design is (?:done|handled|managed) in\b/i,
];

/** How the answer to "what can you do?" opens: AiVA describing itself. */
const CAPABILITY_OPENING = new RegExp(LEAD_IN + String.raw`(?:I\s+can|I\s+could|Here(?:'s|’s|\s+is)\s+what\s+I\s+can)\b`, 'i');

/**
 * The answer to "what can you do?": AiVA describing itself, closing on a
 * question back to the user ("I can draft slide content … What would you like to
 * do?"). It opens with a statement, so the shared question detector, which only
 * reads openings, lets it through. Needs both ends — slide copy that merely
 * starts "I can …" never ends by asking the reader what they want. The two ends
 * are checked separately rather than as one "opening … ?$" pattern, which
 * backtracks super-linearly on long text.
 */
function isCapabilityAnswer(text: string): boolean {
  return text.endsWith('?') && CAPABILITY_OPENING.test(text);
}

/** Longest reply {@link isClarification} will judge — a clarification is a sentence or three. */
const CLARIFICATION_MAX_LENGTH = 320;
/** Most lines {@link isClarification} will judge; slide copy is usually more. */
const CLARIFICATION_MAX_LINES = 2;
const QUESTION_OPENING = /^(?:what|which|who|how|should|would|shall|do|does|did|can|could|is|are|will)\b/i;
/** A question put to the reader mid-sentence: "I don't have the figures — could you share them?" */
const ASKS_THE_READER = /\b(?:could|can|would|will|do|did|should)\s+you\b/i;
/** A friendly opener before the question: "Sure!", "Happy to help —". */
const QUESTION_LEAD_IN = /^(?:sure|of course|certainly|absolutely|happy to help|glad to help)\b[\s!,.—–-]*/i;
/** Input asked for without a question mark: "Paste the text you'd like me to tighten." */
const WANTS_INPUT = /\byou(?:'d|’d|\s+would)\s+like\s+me\s+to\b/i;

// The word lists below are matched on plain words (see `wordsOf`) rather than
// as regex alternations: each list as one pattern exceeded Sonar's regex
// complexity cap, and "verb … anywhere later" as a pattern backtracks.

/** What a clarification asks about: the reader, or something the request needs. */
const ABOUT_THE_REQUEST = new Set([
  'you',
  'your',
  'slide',
  'slides',
  'deck',
  'presentation',
  'brief',
  'topic',
  'audience',
  'point',
  'points',
  'bullet',
  'bullets',
  'figure',
  'figures',
  'number',
  'numbers',
  'detail',
  'details',
  'content',
  'title',
  'tone',
  // "What should it cover?", "What's it about?" (AIVA-9186)
  'cover',
  'about',
  'include',
]);
/**
 * What may follow the question: the drafting prompt's "offer to rework the
 * current slide's text instead", or a prompt for the details it needs.
 */
const FOLLOW_UP_OPENINGS: readonly string[] = [
  'or',
  'alternatively',
  "if you'd like",
  "if you'd prefer",
  'if you would like',
  'if you would prefer',
  'i can',
  'i could',
  // "You could also ask me to…", "For example, I could…" (AIVA-9186)
  'you can',
  'you could',
  'for example',
  'for instance',
  'tell me',
  'let me know',
  'share',
  'give me',
];
/** How a sentence asking for input opens: "Share them…", "Please paste…". */
const REQUEST_OPENINGS: readonly string[] = [
  'paste',
  'share',
  'provide',
  'send',
  'upload',
  'give me',
  'tell me',
  'let me know',
];
/**
 * What makes such a sentence a request to the user rather than slide copy
 * ("Share results with the board"): it says where to put the input, or what
 * AiVA will do with it — "…and I'll draft the slide", "…here".
 */
const ADDRESSED_TO_USER_PHRASES: readonly string[] = [
  'here',
  'below',
  'in chat',
  'in the chat',
  'into chat',
  'into the chat',
  "i'll",
  'i will',
  'i can',
  'so i',
  'so that i',
  'and i',
];

/** `text` as lower-case words joined by single spaces, curly apostrophes made straight. */
function wordsOf(text: string): string {
  return (text.toLowerCase().replace(/’/g, "'").match(/[a-z0-9']+/g) ?? []).join(' ');
}

/** True when `words` (from {@link wordsOf}) opens with one of `phrases`, as whole words. */
function opensWith(words: string, phrases: readonly string[]): boolean {
  return phrases.some((phrase) => words === phrase || words.startsWith(`${phrase} `));
}

/** True when `words` (from {@link wordsOf}) contains one of `phrases`, as whole words. */
function containsPhrase(words: string, phrases: readonly string[]): boolean {
  const padded = ` ${words} `;
  return phrases.some((phrase) => padded.includes(` ${phrase} `));
}

/** "Share them and I'll draft the slide." — an optional "please", a request verb, addressed to the user. */
function isRequestForInput(sentence: string): boolean {
  const words = wordsOf(sentence).replace(/^please /, '');
  return opensWith(words, REQUEST_OPENINGS) && containsPhrase(words, ADDRESSED_TO_USER_PHRASES);
}

function isFollowUpAfterQuestion(sentence: string): boolean {
  return opensWith(wordsOf(sentence), FOLLOW_UP_OPENINGS);
}

function isAboutTheRequest(sentence: string): boolean {
  return wordsOf(sentence)
    .split(' ')
    .some((word) => ABOUT_THE_REQUEST.has(word));
}

/**
 * The reply's sentences, each without a friendly opener; an opener alone ("Sure!") is dropped.
 * A dash also ends a sentence, so "I can draft that — what should it cover?" yields the question.
 */
function clarificationSentences(text: string): string[] {
  // Disjoint classes, so no backtracking: a greedy `[^.?!—–]+` already ends at a terminator or the end.
  return (text.match(/[^.?!—–]+[.?!—–]*/g) ?? [])
    .map((s) => s.trim().replace(QUESTION_LEAD_IN, '').trim())
    .filter(Boolean);
}

/**
 * A reply asking the user for what it needs instead of giving slide content
 * (AIVA-8972) — e.g. the no-brief reply ppt_draft_content is told to send: "What
 * should this slide cover? I can also rework the current slide's text." Judged
 * on the reply's shape rather than one phrasing, because the model words it
 * freely. The shared clarifying-question detector only knows Word-style
 * phrasings ("…would you like…", "…should I…") and is not widened, as Word
 * shares it.
 *
 * Short (a sentence or three, at most two lines), and either:
 *  - a sentence asking for input — "Share them and I'll draft it", "Paste the
 *    text you'd like me to tighten"; or
 *  - a last question put to the reader (a question opening, or "could you…")
 *    about the reader or what the request needs, followed by nothing but an
 *    offer or a prompt for details.
 *
 * So slide copy keeps its buttons: a question title followed by content ("What
 * drives growth? Renewals, new mandates…"), and a question that names neither
 * the reader nor the request ("Why automation?").
 */
function isClarification(text: string): boolean {
  // Blank lines don't count: a question and its offer in two paragraphs are still two lines.
  const lines = text.split('\n').filter((line) => line.trim()).length;
  if (text.length > CLARIFICATION_MAX_LENGTH || lines > CLARIFICATION_MAX_LINES) return false;
  const sentences = clarificationSentences(text);
  if (sentences.some((s) => isRequestForInput(s) || WANTS_INPUT.test(s))) return true;
  let last = sentences.length - 1;
  while (last >= 0 && !sentences[last]?.endsWith('?')) last--;
  if (last === -1) return false;
  if (!sentences.slice(last + 1).every(isFollowUpAfterQuestion)) return false;
  const question = sentences[last] ?? '';
  return (QUESTION_OPENING.test(question) || ASKS_THE_READER.test(question)) && isAboutTheRequest(question);
}

/**
 * A reply that talks to the user instead of being slide content — a request
 * for input, AiVA describing what it cannot do, or the Deckorate hand-off.
 *
 * Placing "Please paste the slide text here…" on a slide is meaningless, but
 * it has no question mark, so the shared question detector misses it. Matched
 * only at the start of a SHORT reply, and a "paste / share …" opening must also
 * be addressed to the user, so slide copy that opens with the same verb
 * ("Share results with the board", "Select the right partner") keeps its button.
 */
export function isNotSlideContent(text: string): boolean {
  const t = text.trim();
  if (!t || t.length > NOT_CONTENT_MAX_LENGTH) return false;
  return (
    NOT_SLIDE_CONTENT_PATTERNS.some((re) => re.test(t)) ||
    isCapabilityAnswer(t) ||
    isClarification(t) ||
    isPlaceholderTemplate(t)
  );
}

/** Trailing filler a placeholder line carries instead of content. */
const PLACEHOLDER_FILLERS: readonly string[] = ['tbd', '...', '…'];
/** A bare label: "Point 1:", "Key point 2", "Bullet 3 -", "Step 4". */
const LABEL_ONLY = /^(?:key )?(?:point|bullet|item|step) ?\d+ ?[:.\-–]?$/i;
/** A bare number: "1.", "2)", "3:". */
const NUMBER_ONLY = /^\d+[.):]$/;

/**
 * A line with a label and no content: "Point 1:", "2.", "Bullet 3: [Insert
 * detail]", "- TBD". Plain string steps rather than one regex — the single
 * pattern this replaced was flagged for super-linear backtracking.
 */
function isPlaceholderLine(line: string): boolean {
  let rest = line.replace(/^[-•*]+/, '').replace(/\s+/g, ' ').trim();
  const bracket = rest.indexOf('[');
  if (bracket !== -1 && rest.endsWith(']')) {
    rest = rest.slice(0, bracket).trim();
  } else {
    const filler = PLACEHOLDER_FILLERS.find((f) => rest.toLowerCase().endsWith(f));
    if (filler) rest = rest.slice(0, -filler.length).trim();
  }
  return rest === '' || LABEL_ONLY.test(rest) || NUMBER_ONLY.test(rest);
}

/**
 * An empty outline — "Briefing points / Point 1: / Point 2: / Point 3:" — the
 * model's answer to "draft 3 points" with no topic. Every line is a bare label
 * except, at most, one heading. Nothing on it is worth placing on a slide.
 */
export function isPlaceholderTemplate(text: string): boolean {
  const lines = text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const placeholders = lines.filter(isPlaceholderLine).length;
  return placeholders >= 2 && placeholders >= lines.length - 1;
}

/** Success toast per PowerPoint write handler (spec: "Every write returns a toast"). */
export const PPT_WRITE_SUCCESS_TOASTS: Readonly<Record<string, string>> = {
  [PPT_INSERT_INTO_SLIDE.handler]: 'Inserted into slide',
  [PPT_REPLACE_SELECTION.handler]: 'Slide text replaced',
  [PPT_ADD_SPEAKER_NOTES.handler]: 'Speaker notes updated',
  [PPT_ADD_AS_NEW_SLIDE.handler]: 'New slide added',
  [PPT_COPY.handler]: 'Copied',
};

/** Spec ST-08's wording for a clipboard write that failed. */
export const COPY_FAILED_MESSAGE = 'Could not copy';

/** Gap G-2's approved wording for a host that cannot reach the speaker notes. */
export const NOTES_UNAVAILABLE_MESSAGE =
  "AiVA can't reach the speaker notes in this version of PowerPoint. Open the deck in the PowerPoint desktop app and try again.";

/** A host that cannot add slides through the API (SC-05). Not spec copy — the spec has none for this case. */
export const ADD_SLIDE_UNAVAILABLE_MESSAGE =
  "AiVA can't add slides in this version of PowerPoint. Open the deck in the PowerPoint desktop app and try again.";

/**
 * The toast for a PowerPoint write that failed for a reason the user can fix,
 * or null for any other failure (the caller's generic copy applies).
 *
 * Matched on the error text because that is all `runHandler` carries back —
 * the same approach as `isStaleTargetError` for Outlook.
 */
export function powerPointFailureToast(error: unknown): string | null {
  if (typeof error !== 'string') return null;
  if (error.includes(NO_SLIDE_SELECTED_ERROR)) return 'Select a slide in PowerPoint, then try again.';
  if (error.includes(POWERPOINT_UNAVAILABLE_ERROR))
    return 'Open this presentation in PowerPoint to place the text.';
  if (error.includes(NOTHING_TO_INSERT_ERROR)) return "There's no text in this reply to place on the slide.";
  if (error.includes(SLIDE_FULL_ERROR)) return 'This slide is full. Use Add as new slide to place the text.';
  if (error.includes(NO_TEXT_SHAPE_SELECTED_ERROR)) return 'Select a shape with text in PowerPoint first.';
  if (error.includes(NOTES_UNAVAILABLE_ERROR)) return NOTES_UNAVAILABLE_MESSAGE;
  if (error.includes(ADD_SLIDE_UNAVAILABLE_ERROR)) return ADD_SLIDE_UNAVAILABLE_MESSAGE;
  if (error.includes(COPY_FAILED_ERROR)) return COPY_FAILED_MESSAGE;
  return null;
}
